import * as React from 'react';
import {
  Button,
  Input,
  Switch,
  makeStyles,
  tokens,
  Title3,
  Body1,
  Field,
  Badge,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { Person20Regular, Building20Regular, ShieldTask20Regular } from '@fluentui/react-icons';
import { UserFull, Phone } from '../../../types/user';
import { ApiErrorInfo } from '../../../types/api';
import { useUserValidations } from '../../../hooks/useUserValidations';
import { useCurrentUser } from '../../../contexts/UserContext';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  phonesSection: {
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    paddingTop: tokens.spacingVerticalL,
  },
  phoneItem: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'flex-end',
    marginBottom: tokens.spacingVerticalS,
  },
  phoneFields: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flex: 1,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalL,
  },
});

interface UserFormProps {
  user?: UserFull;
  onSave: (user: UserFull) => void;
  onCancel: () => void;
  loading?: boolean;
  error?: ApiErrorInfo | null;
}

const UserForm: React.FC<UserFormProps> = ({ user, onSave, onCancel, loading, error }) => {
  const styles = useStyles();
  const { validationState, validateEmail, validateCpfCnpj, applyCpfCnpjMaskToValue, resetValidations } = useUserValidations(); // eslint-disable-line @typescript-eslint/no-unused-vars
  const { currentUser } = useCurrentUser();
  const phoneIdCounter = React.useRef(Date.now());

  const [formData, setFormData] = React.useState<UserFull>(
    user ? {
      ...user,
      telefones: user.telefones || []
    } : {
      id: '',
      email: '',
      razaoSocial: '',
      cpfCnpj: '',
      inscricaoEstadual: '',
      inscricaoMunicipal: '',
      tipo: 0, // Usuários cadastrados via UserForm são sempre tipo 0 (usuário)
      plano: 1, // Plano padrão
      ativo: true,
      excluido: false,
      telefones: [],
    }
  );

  // Atualizar formData quando user prop mudar
  React.useEffect(() => {
    if (user) {
      setFormData({
        ...user,
        telefones: user.telefones || []
      });
    }
  }, [user]);

  // Resetar validações quando o usuário muda (edição vs criação)
  React.useEffect(() => {
    resetValidations();
  }, [user, resetValidations]);

  const [debouncedEmail, setDebouncedEmail] = React.useState('');
  const [debouncedCpfCnpj, setDebouncedCpfCnpj] = React.useState('');

  // Debounce para validação de email
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedEmail && debouncedEmail !== user?.email) {
        validateEmail(debouncedEmail);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [debouncedEmail, user?.email, validateEmail]);

  // Debounce para validação de CPF/CNPJ
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedCpfCnpj && debouncedCpfCnpj !== user?.cpfCnpj) {
        validateCpfCnpj(debouncedCpfCnpj);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [debouncedCpfCnpj, user?.cpfCnpj, validateCpfCnpj]);

  const handleInputChange = (field: keyof UserFull, value: any) => {
    if (field === 'cpfCnpj') {
      // Aplicar máscara ao CPF/CNPJ
      const maskedValue = applyCpfCnpjMaskToValue(value);
      setFormData(prev => ({ ...prev, [field]: maskedValue }));
      setDebouncedCpfCnpj(maskedValue);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));

      if (field === 'email') {
        setDebouncedEmail(value);
      }
    }
  };

  const handlePhoneChange = (index: number, field: keyof Phone, value: any) => {
    const newPhones = [...formData.telefones];
    newPhones[index] = { ...newPhones[index], [field]: value };
    setFormData(prev => ({ ...prev, telefones: newPhones }));
  };

  const addPhone = () => {
    phoneIdCounter.current += 1;
    const newPhone: Phone = {
      id: phoneIdCounter.current,
      usuarioId: formData.id,
      tipo: 0,
      tipoDescricao: 'Residencial',
      ddd: '',
      numero: '',
      observacao: '',
      criadoEm: new Date().toISOString(),
      atualizadoEm: new Date().toISOString(),
    };
    setFormData(prev => ({ ...prev, telefones: [...prev.telefones, newPhone] }));
  };

  const removePhone = (index: number) => {
    const newPhones = formData.telefones.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, telefones: newPhones }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Verificar se há validações em andamento
    if (validationState.email.isChecking || validationState.cpfCnpj.isChecking) {
      return; // Aguardar validações terminarem
    }

    // Verificar se há erros de validação
    if (!validationState.email.isValid || !validationState.cpfCnpj.isValid) {
      return; // Não permitir salvar com erros
    }

    // Verificar campos obrigatórios básicos
    if (!formData.email || !formData.razaoSocial || !formData.cpfCnpj) {
      return; // Campos obrigatórios não preenchidos
    }

    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: tokens.spacingHorizontalL, 
        marginBottom: tokens.spacingVerticalL,
        flexWrap: 'wrap'
      }}>
        <Title3 style={{ margin: 0 }}>{user ? 'Editar Usuário' : 'Novo Usuário'}</Title3>
        <Badge
          appearance="filled"
          color={formData.tipo === 0 ? 'brand' : formData.tipo === 2 ? 'important' : 'success'}
          icon={formData.tipo === 0 ? <Person20Regular /> : formData.tipo === 2 ? <ShieldTask20Regular /> : <Building20Regular />}
          style={{ 
            padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
            fontSize: tokens.fontSizeBase200
          }}
        >
          {formData.tipo === 0 ? 'Usuário' : formData.tipo === 2 ? 'Admin' : 'Empresa'}
        </Badge>
      </div>

      {error && (
        <MessageBar intent={error.isAuthError ? "warning" : "error"}>
          <MessageBarBody>
            {error.isNotFound && "Usuário não encontrado. Verifique se o ID está correto."}
            {error.isValidationError && error.message}
            {error.isAuthError && "Acesso negado. Você não tem permissão para esta operação."}
            {!error.isNotFound && !error.isValidationError && !error.isAuthError && error.message}
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.section}>
        <Field
          label="E-mail"
          required
          validationMessage={validationState.email.error}
          validationState={validationState.email.error ? 'error' : 'none'}
        >
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
            disabled={validationState.email.isChecking}
          />
        </Field>

        <Field label={formData.tipo === 0 ? "Nome" : "Razão Social"} required>
          <Input
            value={formData.razaoSocial}
            onChange={(e) => handleInputChange('razaoSocial', e.target.value)}
            required
          />
        </Field>

        <Field
          label="CPF/CNPJ"
          required
          validationMessage={validationState.cpfCnpj.error}
          validationState={validationState.cpfCnpj.error ? 'error' : 'none'}
        >
          <Input
            value={formData.cpfCnpj}
            onChange={(e) => handleInputChange('cpfCnpj', e.target.value)}
            placeholder="000.000.000-00 ou 00.000.000/0000-00"
            required
            disabled={validationState.cpfCnpj.isChecking}
          />
        </Field>

        {formData.tipo === 1 && (
          <>
            <Field label="Inscrição Estadual">
              <Input
                value={formData.inscricaoEstadual}
                onChange={(e) => handleInputChange('inscricaoEstadual', e.target.value)}
              />
            </Field>

            <Field label="Inscrição Municipal">
              <Input
                value={formData.inscricaoMunicipal}
                onChange={(e) => handleInputChange('inscricaoMunicipal', e.target.value)}
              />
            </Field>
          </>
        )}

        {currentUser?.tipo === 1 && (
          <Field label="Ativo">
            <Switch
              checked={formData.ativo}
              onChange={(e) => handleInputChange('ativo', e.currentTarget.checked)}
            />
          </Field>
        )}
      </div>

      <div className={`${styles.section} ${styles.phonesSection}`}>
        <Body1>Telefones</Body1>
        {(formData.telefones || []).map((phone, index) => (
          <div key={`phone-${phone.id}`} className={styles.phoneItem}>
            <div className={styles.phoneFields}>
              <Field label="DDD">
                <Input
                  value={phone.ddd}
                  onChange={(e) => handlePhoneChange(index, 'ddd', e.target.value)}
                  placeholder="11"
                  style={{ width: '60px' }}
                />
              </Field>
              <Field label="Número">
                <Input
                  value={phone.numero}
                  onChange={(e) => handlePhoneChange(index, 'numero', e.target.value)}
                  placeholder="99999-9999"
                  style={{ flex: 1 }}
                />
              </Field>
              <Field label="Tipo">
                <select
                  value={phone.tipo !== undefined ? String(phone.tipo) : "0"}
                  onChange={(e) => {
                    const tipoValue = parseInt(e.target.value) || 0;
                    const tipoDescricaoMap: { [key: number]: string } = {
                      0: 'Residencial',
                      1: 'Comercial',
                      2: 'Celular',
                      3: 'Recado',
                      4: 'Fax',
                      5: 'Outros',
                      6: 'TelefoneEFax'
                    };
                    const newPhones = [...formData.telefones];
                    newPhones[index] = { 
                      ...newPhones[index], 
                      tipo: tipoValue,
                      tipoDescricao: tipoDescricaoMap[tipoValue]
                    };
                    setFormData(prev => ({ ...prev, telefones: newPhones }));
                  }}
                  style={{ 
                    width: '150px',
                    height: '32px',
                    padding: '0 8px',
                    borderRadius: tokens.borderRadiusMedium,
                    border: `1px solid ${tokens.colorNeutralStroke1}`,
                    fontFamily: tokens.fontFamilyBase,
                    fontSize: tokens.fontSizeBase300
                  }}
                >
                  <option value="0">Residencial</option>
                  <option value="1">Comercial</option>
                  <option value="2">Celular</option>
                  <option value="3">Recado</option>
                  <option value="4">Fax</option>
                  <option value="5">Outros</option>
                  <option value="6">Telefone e Fax</option>
                </select>
              </Field>
            </div>
            <Button
              appearance="subtle"
              onClick={() => removePhone(index)}
            >
              Remover
            </Button>
          </div>
        ))}
        <Button appearance="outline" onClick={addPhone}>
          Adicionar Telefone
        </Button>
      </div>

      <div className={styles.actions}>
        <Button appearance="subtle" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          type="submit"
          appearance="primary"
          disabled={
            loading ||
            validationState.email.isChecking ||
            validationState.cpfCnpj.isChecking ||
            !validationState.email.isValid ||
            !validationState.cpfCnpj.isValid ||
            !formData.email ||
            !formData.razaoSocial ||
            !formData.cpfCnpj
          }
        >
          {loading ? 'Salvando...' :
           validationState.email.isChecking || validationState.cpfCnpj.isChecking ? 'Validando...' :
           'Salvar'}
        </Button>
      </div>
    </form>
  );
};

export default UserForm;